/**
 * `quality-*` — the four read-only quality dashboards · CLM-40 (D-B11, adopted
 * 9 Sep 2026 per the critique recommendations; Gerry may overturn).
 *
 * Four `GET /v1/quality/*` reads behind the **Search quality** surface, one per
 * panel, so a 403 or 501 on one panel never blanks the other three and every
 * list pages independently (Q4). Nothing here writes: `writes_audit_row: false`
 * on every entry, no DDL (0085 is CLM-39's, 0086 unallocated — Q1), no new
 * action (Q2 / G19 — `clause:review_match` for search weakness, stale
 * judgements and outbound health; `contract:read` under the `root_contract_key`
 * predicate for unresolved obligation owners).
 *
 * ## Every figure is a named SQL aggregate
 *
 * FR-27 (explainable): each number on the wire is one `count`/`min` in
 * `apps/api/src/data/quality-queries.ts`, and the SPA computes nothing — it
 * prints wire fields. O-6: identical inputs yield identical bodies; `now()`
 * appears only in the window predicate, never in an `ORDER BY` or a cursor.
 *
 * ## Corpus vs scoped
 *
 * `clause_search_log`, `clause_search_feedback`, `vw_clause_search_judgement`
 * and `role_mapping` carry no contract column, so search weakness and stale
 * judgements are corpus-level and their lists report `total_in_scope: null`.
 * `vw_outbound_health` is unscoped by ratified design (0048: "a queue depth is a
 * fact about the system, not about a viewer"); the outbound per-day series and
 * row list read `vw_outbound_queue_scope` under the scope predicate (G20).
 * Obligation owners are scoped on `root_contract_key` throughout — totals and
 * rows — so a practice-scoped PMO sees only their own fixers' work (O-1).
 *
 * ## Timestamps and dates cross the wire as strings
 *
 * `pg` returns `timestamptz` and `date` as `Date` and `numeric` as a string, so
 * every statement projects `utcIso(...)`, `::text` and `::float8` and these
 * schemas parse exactly that.
 */
import { z } from 'zod';
import { ClauseCodeSchema, ContractKeySchema, IsoDateSchema, IsoTimestampUtcSchema, OpaqueIdSchema, UpnSchema, VersionNoSchema, } from '../primitives.js';
import { PageInfoSchema, PageRequestSchema } from '../pagination.js';
import { ContractTypeConfirmationSchema, ContractTypeSchema, ObligationOwnerStatusSchema, ObligationStatusSchema, SearchJudgementSchema, } from '../vocabulary.js';
/* -------------------------------------------------------------------------- */
/* Shared                                                                      */
/* -------------------------------------------------------------------------- */
/** `window_days`. Server default **30** (applied in the handler, not a Zod `.default()`). */
export const QualityWindowSchema = z.number().int().min(1).max(365);
export const DEFAULT_QUALITY_WINDOW_DAYS = 30;
const Count = z.number().int().min(0);
/* -------------------------------------------------------------------------- */
/* GET /v1/quality/search-weakness                                             */
/* -------------------------------------------------------------------------- */
/**
 * The four weakness signals over `clause_search_log`:
 *  - `zero_result` — `result_count = 0` (index `ix_clause_search_log_zero_result`, 0024);
 *  - `unselected` — shown, never selected, **repeatedly** (`runs >= UNSELECTED_MIN_RUNS`);
 *  - `drift` — the same normalised query ranked a different **clause code** first
 *    within the window (a revision of the same clause is not drift);
 *  - `not_relevant_top` — the top clause was judged `NotRelevant` (reads CLM-39's rows).
 */
export const SEARCH_WEAKNESS_SIGNALS = ['zero_result', 'unselected', 'drift', 'not_relevant_top'];
export const SearchWeaknessSignalSchema = z.enum(SEARCH_WEAKNESS_SIGNALS);
export const SearchWeaknessRequestSchema = z
    .object({
    window_days: QualityWindowSchema.optional(),
    /** Server default `'zero_result'`. */
    signal: SearchWeaknessSignalSchema.optional(),
    page: PageRequestSchema.optional(),
})
    .strict();
export const SearchWeaknessTotalsSchema = z.object({
    window_days: QualityWindowSchema,
    queries: Count,
    unique_queries: Count,
    zero_result: Count,
    unselected: Count,
    drifting: Count,
    not_relevant_top: Count,
});
/** One shape for all four signals; fields a signal does not compute are `null`/`0`. */
export const SearchWeaknessItemSchema = z.object({
    /** `lower(btrim(query_text))` — the normalisation `uq_clause_search_feedback_live` uses (0025). */
    query_key: z.string().min(1).max(400),
    sample_query_text: z.string().min(1).max(400),
    runs: z.number().int().min(1),
    first_seen_utc: IsoTimestampUtcSchema,
    last_seen_utc: IsoTimestampUtcSchema,
    distinct_top_clauses: Count,
    top_clause_code: ClauseCodeSchema.nullable(),
    not_relevant_judgements: Count,
});
export const SearchWeaknessResponseSchema = z.object({
    totals: SearchWeaknessTotalsSchema,
    signal: SearchWeaknessSignalSchema,
    items: z.array(SearchWeaknessItemSchema),
    page: PageInfoSchema,
}).strict();
/* -------------------------------------------------------------------------- */
/* GET /v1/quality/outbound-health                                             */
/* -------------------------------------------------------------------------- */
/** 0045 CHECK on `outbound_queue.target_system`. A new target is CLM-38's DDL plus one enum line here. */
export const OUTBOUND_TARGET_SYSTEMS = ['Qfly', 'XM', 'Teams'];
export const OutboundTargetSystemSchema = z.enum(OUTBOUND_TARGET_SYSTEMS);
/** 0045 CHECK on `outbound_queue.queue_status`. */
export const OUTBOUND_QUEUE_STATUSES = ['Pending', 'Sent', 'Failed', 'Abandoned'];
export const OutboundQueueStatusSchema = z.enum(OUTBOUND_QUEUE_STATUSES);
/** 0045 CHECK on `outbound_queue.target_ref_type`. */
export const OUTBOUND_TARGET_REF_TYPES = ['ClientRecord', 'Opportunity', 'Project', 'Team'];
export const OutboundTargetRefTypeSchema = z.enum(OUTBOUND_TARGET_REF_TYPES);
/** The row list shows only the states that need a human. */
export const OUTBOUND_ATTENTION_STATUSES = ['Failed', 'Abandoned'];
export const OutboundAttentionStatusSchema = z.enum(OUTBOUND_ATTENTION_STATUSES);
export const OutboundHealthRequestSchema = z
    .object({
    window_days: QualityWindowSchema.optional(),
    /** Server default: both `Failed` and `Abandoned`. */
    status: OutboundAttentionStatusSchema.optional(),
    page: PageRequestSchema.optional(),
})
    .strict();
/** One row of `vw_outbound_health` (0048) — the view's six columns, nothing derived. */
export const OutboundHealthCellSchema = z.object({
    target_system: OutboundTargetSystemSchema,
    queue_status: OutboundQueueStatusSchema,
    item_count: Count,
    oldest_utc: IsoTimestampUtcSchema.nullable(),
    max_attempts: z.number().int().min(0).max(100),
    earliest_next_retry_utc: IsoTimestampUtcSchema.nullable(),
});
/** Settled per day per target. **Scoped** (G20) — two viewers may see different series. */
export const OutboundDaySchema = z.object({
    day: IsoDateSchema,
    target_system: OutboundTargetSystemSchema,
    sent: Count,
    failed: Count,
    abandoned: Count,
});
/** Folded in the handler from the S6 cells — no second statement. */
export const OutboundHealthTotalsSchema = z.object({
    window_days: QualityWindowSchema,
    /** Sum of the `Failed` cells' `item_count`. */
    retry_backlog: Count,
    /** Min `oldest_utc` over the `Pending` cells. */
    oldest_pending_utc: IsoTimestampUtcSchema.nullable(),
    /** Sum of the `Abandoned` cells' `item_count`. */
    abandoned_total: Count,
});
/** The `vw_outbound_queue_scope` projection (0049) minus `sent_audit_id`. No `payload` — the view withholds it. */
export const OutboundQueueItemSchema = z.object({
    outbound_id: OpaqueIdSchema,
    contract_key: ContractKeySchema,
    /** `root_contract_key` of the contract — the scope anchor (0008). */
    effective_contract_key: ContractKeySchema,
    target_system: OutboundTargetSystemSchema,
    target_ref_type: OutboundTargetRefTypeSchema,
    target_ref_id: z.string().max(120),
    queue_status: OutboundQueueStatusSchema,
    attempt_count: Count,
    last_attempt_utc: IsoTimestampUtcSchema.nullable(),
    next_retry_utc: IsoTimestampUtcSchema.nullable(),
    last_error: z.string().max(1000).nullable(),
    created_utc: IsoTimestampUtcSchema,
    sent_utc: IsoTimestampUtcSchema.nullable(),
    needs_attention: z.boolean(),
});
export const OutboundHealthResponseSchema = z.object({
    totals: OutboundHealthTotalsSchema,
    /** System-level (the ratified view). */
    cells: z.array(OutboundHealthCellSchema),
    /** Scoped (G20). */
    days: z.array(OutboundDaySchema),
    /** Scoped. */
    items: z.array(OutboundQueueItemSchema),
    page: PageInfoSchema,
}).strict();
/* -------------------------------------------------------------------------- */
/* GET /v1/quality/obligation-owners                                           */
/* -------------------------------------------------------------------------- */
/** No window — an unresolved owner is unresolved until fixed. */
export const ObligationOwnersRequestSchema = z
    .object({
    page: PageRequestSchema.optional(),
})
    .strict();
/** The three role patterns `notify` resolves through `role_mapping` (0043). */
export const ROLE_MAPPING_PATTERNS = ['practice_director', 'pmo', 'elt'];
export const RoleMappingPatternSchema = z.enum(ROLE_MAPPING_PATTERNS);
export const RoleMappingStatusSchema = z.object({
    role_pattern: RoleMappingPatternSchema,
    /** `confirmed_targets > 0` — true exactly when `notify` would not exit 1 for that rule. */
    is_mapped: z.boolean(),
    confirmed_targets: Count,
});
/** **All scoped** on `root_contract_key`. */
export const ObligationOwnersTotalsSchema = z.object({
    unresolved_obligations: Count,
    contracts_affected: Count,
    contracts_without_owner: Count,
});
export const UnresolvedObligationItemSchema = z.object({
    obligation_id: OpaqueIdSchema,
    contract_key: ContractKeySchema,
    root_contract_key: ContractKeySchema,
    contract_type: ContractTypeSchema,
    /** CLM-110 (R-110-23) — rendered beside `contract_type` as its `provisional` tag. */
    contract_type_confirmation: ContractTypeConfirmationSchema,
    description: z.string().max(1000),
    due_date: IsoDateSchema.nullable(),
    obligation_status: ObligationStatusSchema,
    owner_role_text: z.string().max(200).nullable(),
    owner_status: ObligationOwnerStatusSchema,
    contract_owner_upn: UpnSchema.nullable(),
});
export const ObligationOwnersResponseSchema = z.object({
    totals: ObligationOwnersTotalsSchema,
    /** Exactly three rows, in `ROLE_MAPPING_PATTERNS` order sorted by name (`elt`, `pmo`, `practice_director`). */
    role_mappings: z.array(RoleMappingStatusSchema).length(3),
    items: z.array(UnresolvedObligationItemSchema),
    page: PageInfoSchema,
}).strict();
/* -------------------------------------------------------------------------- */
/* GET /v1/quality/stale-judgements                                            */
/* -------------------------------------------------------------------------- */
export const StaleJudgementsRequestSchema = z
    .object({
    page: PageRequestSchema.optional(),
})
    .strict();
/**
 * One row of `vw_clause_search_judgement WHERE is_stale` (0026). Not
 * `ClauseSearchJudgementSchema` — that shape lacks `current_version_no`, and
 * widening it is CLM-39's wire change. `staleness_weight` is projected `::float8`
 * so the JSON number `0.3` reaches the literal.
 */
export const StaleJudgementItemSchema = z.object({
    feedback_id: OpaqueIdSchema,
    clause_code: ClauseCodeSchema,
    clause_id: OpaqueIdSchema,
    judged_version_no: VersionNoSchema,
    /** `null` when the clause has been retired (no current version). */
    current_version_no: VersionNoSchema.nullable(),
    is_stale: z.literal(true),
    staleness_weight: z.literal(0.3),
    query_text: z.string().max(400),
    judgement: SearchJudgementSchema,
    judged_by_upn: UpnSchema,
    judged_utc: IsoTimestampUtcSchema,
    search_id: OpaqueIdSchema.nullable(),
});
export const StaleJudgementsTotalsSchema = z.object({
    stale: Count,
    active: Count,
    oldest_stale_judged_utc: IsoTimestampUtcSchema.nullable(),
});
export const StaleJudgementsResponseSchema = z.object({
    totals: StaleJudgementsTotalsSchema,
    items: z.array(StaleJudgementItemSchema),
    page: PageInfoSchema,
}).strict();
//# sourceMappingURL=quality.js.map